import { useParams, Link } from 'react-router-dom';
import { games, categories } from '@/mocks/games';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { siteConfig } from '@/mocks/siteConfig';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryGames = games.filter((g) => g.categorySlug === slug);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
              <i className="ri-gamepad-line text-4xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-medium text-foreground-900 mb-2">分类不存在</h2>
            <p className="text-foreground-500 mb-6">未找到该游戏分类，请返回首页查看</p>
            <Link to="/" className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const collectionPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - ${siteConfig.name}`,
    description: `${siteConfig.name}${category.name}专区，${category.description}`,
    url: `${siteConfig.url}/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteConfig.name,
      url: siteConfig.url,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionPageSchema) }}
      />

      <header>
        <Navbar />
      </header>

      <main className="min-h-screen pt-16">
        <div className="bg-background-100">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '游戏大厅', to: '/category/qipai' },
              { label: category.name },
            ]}
          />

          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-primary-500">
                <i className={`${category.icon} text-background-50 text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">{category.name}</h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-10 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:text-foreground-900 border border-background-300 hover:border-primary-300'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryGames.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {categoryGames.map((game) => (
                  <div
                    key={game.id}
                    className="group bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden cursor-pointer hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={game.image}
                        alt={game.name}
                        title={`九游娱乐${game.name}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      {game.tag && (
                        <span className={`absolute top-3 left-3 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                          game.tag === '热门' || game.tag === '爆款'
                            ? 'bg-accent-500 text-background-50'
                            : game.tag === '新品'
                              ? 'bg-primary-500 text-background-50'
                              : 'bg-secondary-500 text-background-50'
                        }`}>
                          {game.tag}
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-base font-medium text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                        {game.name}
                      </h3>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-star-fill text-primary-500 text-xs"></i>
                          </div>
                          <span className="text-sm text-foreground-700">{game.rating}</span>
                        </div>
                        <span className="text-xs text-foreground-500">
                          <i className="ri-user-line mr-1"></i>
                          {game.players}玩家
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                  <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无游戏，敬请期待</p>
              </div>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}